import React, { useState } from 'react';
import {
  APP_VIEW_ICONS,
  AppViewType,
  useAppView,
} from '../../../contexts/AppViewContext';
import { PanelView, PanelViewProvider } from '../../PanelView';
import { Popper } from '../../Popper';
import { useOperators } from '../../../hooks/useOperators';
import { useRDPs } from '../../../hooks/useRDPs';
import clsx from 'clsx';
import { AppViewOptions } from './AppViewOptions';
import { EntityOptions } from './EntityOptions';
import { HourglassFullOutlined } from '@material-ui/icons';
import { Backdrop } from '../../Backdrop/Backdrop';

interface AppViewMenuProps {
  loading: boolean;
}

export const AppViewMenu = ({ loading }: AppViewMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const appView = useAppView();

  const handleClose = (resetState: boolean) => {
    setOpen(false);
    if (resetState) appView.resetPendingView();
  };

  const determineDefaultView = () => {
    if (appView.permissions.canViewRDP || appView.permissions.canViewNDP) {
      if (appView.type !== 'NDP') return 'Options';
      else return 'View';
    } else return 'Options';
  };

  const defaultView = determineDefaultView();

  return (
    <React.Fragment>
      <AppViewButton
        ref={setAnchorEl}
        loading={loading}
        onClick={() => setOpen(true)}
      />
      <Backdrop open={open} onClose={() => handleClose(true)} disableBlur>
        <Popper
          className="!bg-card-dark"
          anchorEl={anchorEl}
          isVisible={open}
          onClose={() => handleClose(true)}
          placement="bottom"
          offsetY={-70}
        >
          <PanelViewProvider defaultView={defaultView}>
            <div className="rounded-b overflow-hidden w-[278px] text-white p-2 border border-t-0 tg-border-color">
              <PanelView id="View">
                <AppViewOptions onClose={() => handleClose(false)} />
              </PanelView>
              <PanelView id="Options">
                <EntityOptions onClose={() => handleClose(false)} />
              </PanelView>
            </div>
          </PanelViewProvider>
        </Popper>
      </Backdrop>
    </React.Fragment>
  );
};

interface AppViewButtonProps {
  loading: boolean;
  onClick(): void;
}

const AppViewButton = React.forwardRef<HTMLDivElement, AppViewButtonProps>(
  ({ loading: externalLoading, onClick }, ref) => {
    const appView = useAppView();
    const icon = APP_VIEW_ICONS('small')[appView.type];

    const operators = useOperators();
    const rdps = useRDPs();
    const loading = externalLoading || operators.isLoading || rdps.isLoading;

    const TITLES: Record<AppViewType, string> = {
      NDP: 'National',
      RDP:
        rdps.data.find((rdp) => rdp.code === appView.code)?.name ?? 'Regional',
      Operator:
        operators.data.find((o) => o.id === Number(appView.id ?? '0'))?.name ??
        'Operator',
    };

    const canViewRDP = appView.permissions.canViewRDP && rdps.data.length > 0;

    const canOpenMenu =
      appView.permissions.canViewRDP ||
      canViewRDP ||
      (operators.data.length > 1 && operators.data.length !== 0);

    return (
      <div ref={ref} className="min-w-[260px] max-w-[276px] h-full w-full p-2">
        <button
          className={clsx(
            'inline-flex items-center justify-center gap-3 rounded hover:bg-white/10 p-2 w-full h-full select-none disabled:opacity-30',
            { 'pointer-events-none': !canOpenMenu }
          )}
          onClick={onClick}
          disabled={loading}
          type="button"
        >
          {loading && (
            <span className="inline-flex items-center gap-3 text-left">
              <HourglassFullOutlined className="!h-4 !w-auto" />
              Loading...
            </span>
          )}
          {!loading && (
            <React.Fragment>
              {canOpenMenu && icon}
              {canOpenMenu && TITLES[appView.type]}{' '}
              {!canOpenMenu && operators.data[0] && operators.data[0].name}
            </React.Fragment>
          )}
        </button>
      </div>
    );
  }
);
